import { getComparableCartPrice } from "@app/helpers/departure-helpers";
import { IncludedFees } from "@app/helpers/price";
import * as Search from "@app/helpers/search";
import { getPassengers } from "@app/helpers/udm/get-passengers";
import { SessionStorage } from "@app/lib/bowser-storage/session-storage";
import type { CartPollResponse } from "@app/modules/checkout/types/cart-v2";
import {
  filterTripCartItems,
  getCartV2TripItem
} from "@app/modules/checkout/utils/cart-v2";
import { TripType } from "@app/modules/results/constants/departure-types";
import sentry from "@app/services/sentry-facade";
import * as ErrorTracking from "@app/tracking/error-tracking";
import type { NapiV2CreateCartRequestOptions } from "@app/types/napi-v2-cart-types";
import type { NapiV2Answer } from "@app/types/napi-v2-passengers-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";
import { getClientToken } from "@app/utils/window";

import type { IsSamePriceAsBeforeOptions } from "./cart-base";

import { CartBase } from "./cart-base";

export class CartStandardV2 extends CartBase {
  protected override cart: CartPollResponse | null = null;

  public get departure_type() {
    const trip_item = getCartV2TripItem(this.cart?.items || null);

    if (!trip_item) {
      sentry.captureMessage("cart without trip item", {
        no_sampling: true,
        extra: { cart: JSON.stringify(this.cart) }
      });
      return TripType.BOB;
    }

    return trip_item.metadata.sellable === false ? TripType.BOX : TripType.BOB;
  }

  public async reserve(
    trip_id: string,
    billing_country: string | undefined,
    passengers: PassengerSeatSelectionInstance[]
  ): Promise<void> {
    const search_passengers = getPassengers(this.search);

    // Outound
    if (!this.search.outbound_id && this.search.return_date) {
      // With UDM, users don't select an outbound id and a return id but
      // a single trip id. Thus we should not create a cart on Napi when selecting
      // the outbound. Instead, the selected trip id on the outbound page should
      // be passed when polling return legs.
      // In order to integrate UDM while still supporting legacy departures, we
      // create a fake cart with the selected trip_id when users pick a outbound
      // trip id.
      // TODO-UDM: Once fully adopted, the cart creation step should be remove
      // during the outbound step
      this.cart = {
        id: "udm_outbound_cart_id",
        // @ts-expect-error
        items: [{ type: "trip", id: trip_id }]
      };
      return Promise.resolve();
    }

    try {
      const options: Partial<NapiV2CreateCartRequestOptions> = {
        billing_country
      };
      const client_token = getClientToken(SessionStorage.fromWindow(window));
      if (client_token) {
        options.recorrido_user_token = client_token;
      }

      const { id: cart_id } = await this.api.createCartV2(options);

      await this.api.addTripToCartV2(cart_id, trip_id, search_passengers);

      if (this.search.discount_code) {
        // If the api call adding the discount code fails, we just continue without adding it, the user will be able to add it manually on the checkout page
        try {
          await this.api.addDiscountCodeV2(cart_id, this.search.discount_code);
        } catch {}
      }
      const poll_cart_response = await this.api.pollCartV2(cart_id);

      await this.applySeatSelection(trip_id, passengers, poll_cart_response);
    } catch (err) {
      if (!err.transaction) {
        err.transaction = ErrorTracking.Transaction.CREATE_CART;
      }

      throw err;
    }
  }

  private async applySeatSelection(
    trip_id: string,
    passengers: PassengerSeatSelectionInstance[],
    poll_cart_response: CartPollResponse | undefined
  ) {
    this.cart = poll_cart_response || null;
    if (!this.cart) {
      return;
    }

    const has_selected_seats = passengers.some(p => p.selected_seats?.length);

    if (has_selected_seats) {
      const trip_item = filterTripCartItems(this.cart.items)[0];
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const ticket_types = trip_item.ticket_types;

      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const updated_passengers = trip_item.passengers.map(
        (passenger, index) => {
          // API doesn't accept null values
          Object.keys(passenger).forEach(
            key =>
              passenger[key as keyof typeof passenger] === null &&
              delete passenger[key as keyof typeof passenger]
          );

          const passenger_to_add = {
            ...passenger,
            // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            selected_seats: passengers[index].selected_seats
          };

          const answers_array = Object.entries(
            // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            passengers[index].questions
          ).map<NapiV2Answer>(([key, value]) => {
            return {
              question_key: key,
              value: value
            } as NapiV2Answer;
          });

          if (answers_array.length) {
            passenger_to_add["answers"] = answers_array;
          }

          return passenger_to_add;
        }
      );

      try {
        await this.api.updateCartTripV2(this.cart.id, trip_id, {
          passengers: updated_passengers,
          ticket_types: ticket_types
        });
      } catch (err) {
        err.transaction = ErrorTracking.Transaction.UPDATE_CART_PASSENGERS;
        throw err;
      }
    }
  }

  public isSamePriceAsBefore(
    selected_price: number,
    options: IsSamePriceAsBeforeOptions = {}
  ): boolean {
    // - Don't raise `price_has_changed` error for a roundtrip's outbound. We choose to not display
    // a price confirmation modal on the outbound page because the departure price you see at this point
    // is the cheapest combination of outbound+return you can have for the selected outbound.
    const is_roundtrip = Search.isRoundtrip(this.search);

    if (!this.cart || (is_roundtrip && !Search.isReturn(this.search))) {
      return true;
    }

    const { include_fees = IncludedFees.NONE } = options;

    const ceiled_departure_price = Math.ceil(selected_price / 100) * 100;

    const cart_price = getComparableCartPrice(this.cart.charges, {
      include_fees
    });

    if (cart_price > ceiled_departure_price) {
      return false;
    }

    return true;
  }
}
